// The art font catalog: Google Fonts families (open licenses) loaded on demand. A text
// layer names one of them; an unknown family renders in the default. The canvas only
// paints with a face once it is loaded, so the stage, the thumbnails and the export all
// wait for `ensureFonts` — an export never goes out in a fallback face.
export const DEFAULT_FONT = 'Inter';

export const FONT_CATALOG = [
  { family: 'Inter', category: 'sans', weights: [300, 400, 500, 600, 700, 800, 900], italic: true },
  { family: 'Poppins', category: 'sans', weights: [300, 400, 500, 600, 700, 800, 900], italic: true },
  { family: 'Montserrat', category: 'sans', weights: [300, 400, 500, 600, 700, 800, 900], italic: true },
  { family: 'DM Sans', category: 'sans', weights: [400, 500, 700], italic: true },
  { family: 'Manrope', category: 'sans', weights: [300, 400, 500, 600, 700, 800] },
  { family: 'League Spartan', category: 'sans', weights: [300, 400, 500, 600, 700, 800] },
  { family: 'Space Grotesk', category: 'sans', weights: [300, 400, 500, 600, 700] },
  { family: 'Syne', category: 'display', weights: [400, 500, 600, 700, 800] },
  { family: 'Bebas Neue', category: 'display', weights: [400] },
  { family: 'Anton', category: 'display', weights: [400] },
  { family: 'Oswald', category: 'display', weights: [300, 400, 500, 600, 700] },
  { family: 'Archivo Black', category: 'display', weights: [400] },
  { family: 'Playfair Display', category: 'serif', weights: [400, 500, 600, 700, 800, 900], italic: true },
  { family: 'DM Serif Display', category: 'serif', weights: [400], italic: true },
  { family: 'Fraunces', category: 'serif', weights: [300, 400, 500, 600, 700, 800, 900], italic: true },
  { family: 'Lora', category: 'serif', weights: [400, 500, 600, 700], italic: true },
  { family: 'Instrument Serif', category: 'serif', weights: [400], italic: true },
  { family: 'Caveat', category: 'handwriting', weights: [400, 500, 600, 700] },
  { family: 'Pacifico', category: 'handwriting', weights: [400] },
  { family: 'Permanent Marker', category: 'handwriting', weights: [400] },
];
export const FONT_CATEGORIES = ['sans', 'display', 'serif', 'handwriting'];
const GENERIC = { sans: 'sans-serif', display: 'sans-serif', serif: 'serif', handwriting: 'cursive' };
const BY_FAMILY = new Map(FONT_CATALOG.map(entry => [entry.family, entry]));

export const fontEntry = family => BY_FAMILY.get(family) || BY_FAMILY.get(DEFAULT_FONT);
export const isCatalogFont = family => BY_FAMILY.has(family);
export function nearestWeight(family, weight) {
  const { weights } = fontEntry(family);
  return weights.reduce((best, candidate) => (Math.abs(candidate - weight) < Math.abs(best - weight) ? candidate : best), weights[0]);
}
export const cssFamily = family => `"${fontEntry(family).family}", ${GENERIC[fontEntry(family).category]}`;
// The CSS/canvas shorthand for a text style at a pixel size.
export function cssFont(style, size = style.font_size) {
  const family = fontEntry(style.font_family).family;
  return `${style.italic && fontEntry(family).italic ? 'italic ' : ''}${nearestWeight(family, style.font_weight)} ${size}px ${cssFamily(family)}`;
}

export function googleFontsUrl(family) {
  const entry = fontEntry(family);
  const name = entry.family.replace(/ /g, '+');
  if (entry.weights.length === 1 && !entry.italic) return `https://fonts.googleapis.com/css2?family=${name}&display=swap`;
  const axes = entry.italic
    ? `ital,wght@${[0, 1].flatMap(italic => entry.weights.map(weight => `${italic},${weight}`)).join(';')}`
    : `wght@${entry.weights.join(';')}`;
  return `https://fonts.googleapis.com/css2?family=${name}:${axes}&display=swap`;
}

const sheets = new Map();
const canLoad = () => typeof document !== 'undefined' && typeof window !== 'undefined' && !!document.fonts && !/jsdom/i.test(navigator.userAgent || '');

// Adds the family's stylesheet once; resolves when its @font-face rules are known.
export function loadFontSheet(family) {
  const entry = fontEntry(family);
  if (!canLoad()) return Promise.resolve(false);
  if (sheets.has(entry.family)) return sheets.get(entry.family);
  const promise = new Promise(resolve => {
    const existing = document.querySelector(`link[data-art-font="${entry.family}"]`);
    if (existing?.sheet) { resolve(true); return; }
    const link = existing || document.createElement('link');
    link.addEventListener('load', () => resolve(true), { once: true });
    // A failed sheet is removed, so the next try adds a fresh link instead of waiting on a dead one.
    link.addEventListener('error', () => { sheets.delete(entry.family); link.remove(); resolve(false); }, { once: true });
    if (!existing) {
      link.rel = 'stylesheet';
      link.href = googleFontsUrl(entry.family);
      link.dataset.artFont = entry.family;
      document.head.appendChild(link);
    }
  });
  sheets.set(entry.family, promise);
  return promise;
}

// Every face the text layers of a document (or of one page) use, with the text they show,
// so the subsets their characters need (accents included) are fetched too.
export function documentFonts(doc, pageId = null) {
  const faces = new Map();
  (doc?.pages || []).filter(page => !pageId || page.id === pageId).forEach(page => page.layers.forEach(layer => {
    if (layer.type !== 'text') return;
    const family = fontEntry(layer.style.font_family).family;
    const weight = nearestWeight(family, layer.style.font_weight);
    const italic = !!layer.style.italic && !!fontEntry(family).italic;
    const key = `${family}|${weight}|${italic}`;
    const face = faces.get(key) || { family, weight, italic, text: '' };
    face.text = `${face.text}${layer.text}`.slice(0, 400);
    faces.set(key, face);
  }));
  return [...faces.values()];
}

const SAMPLE = 'AaÁáÉéÍíÓóÚúÃãÕõÇç0123456789';
const faceLoads = new Map();
// Loads the faces; resolves with the families that could not be loaded (empty when all are).
export async function ensureFonts(faces) {
  if (!canLoad()) return { ok: true, missing: [] };
  const results = await Promise.all(faces.map(async face => {
    const sheet = await loadFontSheet(face.family);
    if (!sheet) return face.family;
    const text = `${SAMPLE}${face.text || ''}`;
    const key = `${face.family}|${face.weight}|${face.italic}|${text}`;
    if (!faceLoads.has(key)) faceLoads.set(key, document.fonts.load(cssFont({ font_family: face.family, font_weight: face.weight, italic: face.italic }, 32), text).catch(() => []));
    const loaded = await faceLoads.get(key);
    if (!loaded.length) { faceLoads.delete(key); return face.family; }
    return null;
  }));
  const missing = [...new Set(results.filter(Boolean))];
  return { ok: !missing.length, missing };
}
export const ensureDocumentFonts = (doc, pageId) => ensureFonts(documentFonts(doc, pageId));

// Fires when the browser finishes loading faces, so measured layouts can be redone.
export function onFontsLoaded(callback) {
  if (!canLoad()) return () => {};
  document.fonts.addEventListener('loadingdone', callback);
  return () => document.fonts.removeEventListener('loadingdone', callback);
}
